import { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useScrollAnimation, useSound, NAV_NOTES, useHorizontalSlide, PANELS, panelIndex } from './hooks';
import { NoteKey } from './hooks/useSound';
import type { Panel } from './hooks';

// Components (to be migrated)
import Navigation from './components/Navigation';
import Hero from './components/Hero';
import PainPoints from './components/PainPoints';
import ServiceCard from './components/ServiceCard';
import Timeline from './components/Timeline';
import BentoGrid from './components/BentoGrid';
import TeamMembers from './components/TeamMembers';
import ContactForm from './components/ContactForm';
import Footer from './components/Footer';
import AnimatedCounter from './components/AnimatedCounter';
import LoginPortalButton from './components/LoginPortalButton';
import ChatBubble from '@/components/chatbot/ChatBubble';
import BlogPanel from './components/BlogPanel';
import ProductsPanel from './components/ProductsPanel';
import PortfolioPanel from './components/PortfolioPanel';
import SlideIndicator from './components/SlideIndicator';
import LanguageSwitcher from '@/components/LanguageSwitcher';

// Import landing styles
import './styles/landing.css';

// Tauri v2 runtime detection
const IS_TAURI = typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;

export default function LandingPage() {
  const { t, i18n } = useTranslation('landing');
  const navigate = useNavigate();
  const { soundEnabled, toggleSound, playNote } = useSound();
  const [searchParams, setSearchParams] = useSearchParams();

  // Sync URL when panel changes
  const handlePanelChange = useCallback((panel: Panel) => {
    if (panel === 'landing') {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ panel }, { replace: true });
    }
  }, [setSearchParams]);

  // Horizontal slide navigation
  const initialPanel = (searchParams.get('panel') as Panel) || 'landing';
  const { activePanel, slideLeft, slideRight, slideTo } = useHorizontalSlide({
    initialPanel: PANELS.includes(initialPanel) ? initialPanel : 'landing',
    onPanelChange: handlePanelChange,
  });

  // Mouse position for hero effects
  const heroRef = useRef<HTMLDivElement>(null);
  const [mousePos, setMousePos] = useState({ x: 0.5, y: 0.5 });

  // Section visibility for scroll animations
  const [painPointsRef, painPointsVisible] = useScrollAnimation<HTMLElement>();
  const [challengeRef, challengeVisible] = useScrollAnimation<HTMLElement>();
  const [bridgeRef, bridgeVisible] = useScrollAnimation<HTMLElement>();
  const [servicesRef, servicesVisible] = useScrollAnimation<HTMLElement>();
  const [whyRef, whyVisible] = useScrollAnimation<HTMLElement>();
  const [teamRef, teamVisible] = useScrollAnimation<HTMLElement>();
  const [ctaRef, ctaVisible] = useScrollAnimation<HTMLElement>();

  // Mouse tracking for hero
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (heroRef.current) {
        const rect = heroRef.current.getBoundingClientRect();
        setMousePos({
          x: (e.clientX - rect.left) / rect.width,
          y: (e.clientY - rect.top) / rect.height,
        });
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Language handlers
  const language = i18n.language;
  const setLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('i18n_language', lang);
  };

  // Nav items with notes
  const navItems = [
    { label: t('nav.services'), href: '#services', note: NAV_NOTES[0] },
    { label: t('nav.products'), href: '#products', note: NAV_NOTES[1], onAction: () => slideTo('products') },
    { label: t('nav.portfolio'), href: '#portfolio', note: NAV_NOTES[2], onAction: () => slideTo('portfolio') },
    { label: t('nav.stories'), href: '#stories', note: NAV_NOTES[3], onAction: () => slideTo('blog') },
    { label: t('nav.about'), href: '#why', note: NAV_NOTES[4] || ('C6' as NoteKey) },
    { label: t('nav.team'), href: '#team', note: 'G5' as NoteKey },
    { label: t('nav.contact'), href: '#contact', note: NAV_NOTES[5] || ('D6' as NoteKey) },
  ];

  // Service icons mapping
  const serviceIcons = ['embedded', 'hardware', 'aiot', 'cloud'];

  // Get array translations
  const painPointProblems = t('painPoints.problems', { returnObjects: true }) as string[];
  const challengeStats = t('challenge.stats', { returnObjects: true }) as Array<{
    value: string;
    suffix: string;
    label: string;
  }>;
  const bridgeSteps = t('bridge.steps', { returnObjects: true }) as Array<{
    title: string;
    description: string;
  }>;
  const serviceItems = t('services.items', { returnObjects: true }) as Array<{
    title: string;
    description: string;
    details: string;
  }>;
  const whyPoints = t('why.points', { returnObjects: true }) as Array<{
    title: string;
    description: string;
  }>;
  const teamMembers = t('team.members', { returnObjects: true }) as Array<{
    name: string;
    role: string;
    bio: string;
  }>;

  // Filter out slide-action items from footer nav
  const footerNavItems = navItems
    .filter((item) => !item.onAction)
    .map((item) => ({ label: item.label, href: item.href }));

  // Compute slide indicator labels and visibility
  const activePanelIdx = panelIndex(activePanel);
  const leftPanelLabel = activePanelIdx > 0
    ? t(`${PANELS[activePanelIdx - 1]}.slideHint`, { defaultValue: PANELS[activePanelIdx - 1] })
    : '';
  const rightPanelLabel = activePanelIdx < PANELS.length - 1
    ? t(`${PANELS[activePanelIdx + 1]}.slideHint`, { defaultValue: PANELS[activePanelIdx + 1] })
    : '';

  // Tauri desktop app: Hero-only with Sign In / Sign Up
  if (IS_TAURI) {
    return (
      <div className="h-screen overflow-hidden bg-[var(--navy-deep)] text-white">
        <div ref={heroRef}>
          <Hero
            tagline={t('hero.tagline')}
            slogan={t('hero.slogan')}
            ctaText={t('auth:signIn', 'Sign In')}
            contactText={t('auth:signUp', 'Sign Up')}
            scrollText=""
            mouseX={mousePos.x}
            mouseY={mousePos.y}
            onPlayNote={() => {}}
            onCtaClick={() => navigate('/login')}
            onContactClick={() => navigate('/login')}
          />
        </div>
        <div className="fixed bottom-6 right-6 z-50">
          <LanguageSwitcher />
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-hidden">
      {/* Four-panel horizontal slider */}
      <div
        className="flex w-[400vw] h-full transition-transform duration-700 ease-in-out"
        style={{ transform: `translateX(-${activePanelIdx * 100}vw)` }}
      >
        {/* Panel 0: Products */}
        <div className="w-screen h-full">
          <ProductsPanel onSlideRight={slideRight} />
        </div>

        {/* Panel 1: Portfolio */}
        <div className="w-screen h-full">
          <PortfolioPanel onSlideRight={slideRight} />
        </div>

        {/* Panel 2: Landing page */}
        <div className="w-screen h-full overflow-y-auto bg-[var(--navy-deep)] text-white landing-scrollbar">
          {/* Navigation - sticky within this scroll container */}
          <Navigation
            language={language}
            onSetLanguage={setLanguage}
            soundEnabled={soundEnabled}
            onToggleSound={toggleSound}
            onPlayNote={playNote}
            navItems={navItems}
            contactLabel={t('nav.contact')}
          />

          {/* Hero Section */}
          <div ref={heroRef}>
            <Hero
              tagline={t('hero.tagline')}
              slogan={t('hero.slogan')}
              ctaText={t('hero.cta')}
              contactText={t('hero.contact')}
              scrollText={t('hero.scroll')}
              mouseX={mousePos.x}
              mouseY={mousePos.y}
              onPlayNote={playNote}
            />
          </div>

          {/* Pain Points / Fill Your Gaps Section */}
          <section ref={painPointsRef} id="pain-points">
            <PainPoints
              title={t('painPoints.title')}
              subtitle={t('painPoints.subtitle')}
              problems={painPointProblems}
              solution={t('painPoints.solution')}
              isVisible={painPointsVisible}
            />
          </section>

          {/* Challenge Section */}
          <section
            ref={challengeRef}
            id="challenge"
            className="py-32 px-6 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[var(--cyan-electric)]/5 to-transparent" />

            <div
              className={`max-w-6xl mx-auto transition-all duration-1000 ${
                challengeVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-8">{t('challenge.title')}</h2>

              <p className="text-xl text-gray-400 mb-4 max-w-3xl">{t('challenge.description')}</p>

              <p className="text-2xl font-semibold text-[var(--cyan-electric)] mb-16">
                {t('challenge.highlight')}
              </p>

              {/* Stats grid */}
              <div className="grid md:grid-cols-3 gap-8">
                {challengeStats.map((stat, i) => (
                  <div
                    key={i}
                    className="card-3d p-8 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:border-[var(--cyan-electric)]/50 transition-all duration-500"
                    style={{
                      transitionDelay: `${i * 100}ms`,
                      opacity: challengeVisible ? 1 : 0,
                      transform: challengeVisible ? 'translateY(0)' : 'translateY(20px)',
                    }}
                  >
                    <div className="text-5xl font-bold gradient-text mb-2">
                      <AnimatedCounter
                        value={parseInt(stat.value)}
                        suffix={stat.suffix}
                        isVisible={challengeVisible}
                      />
                    </div>
                    <div className="text-gray-400">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Bridge/Timeline Section */}
          <section ref={bridgeRef} id="bridge">
            <Timeline
              title={t('bridge.title')}
              subtitle={t('bridge.subtitle')}
              steps={bridgeSteps}
              isVisible={bridgeVisible}
            />
          </section>

          {/* Services Section */}
          <section ref={servicesRef} id="services" className="py-32 px-6">
            <div
              className={`max-w-6xl mx-auto transition-all duration-1000 ${
                servicesVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-16 text-center">
                {t('services.title')}
              </h2>

              <div className="grid md:grid-cols-2 gap-6">
                {serviceItems.map((service, i) => (
                  <ServiceCard
                    key={i}
                    title={service.title}
                    description={service.description}
                    details={service.details}
                    icon={serviceIcons[i]}
                    index={i}
                    isVisible={servicesVisible}
                    onPlayNote={playNote}
                  />
                ))}
              </div>
            </div>
          </section>

          {/* Why Fluxconn Section */}
          <section ref={whyRef} id="why">
            <BentoGrid
              title={t('why.title')}
              quote={t('why.quote')}
              points={whyPoints}
              isVisible={whyVisible}
            />
          </section>

          {/* Team Members Section */}
          <section ref={teamRef} id="team">
            <TeamMembers
              title={t('team.title')}
              subtitle={t('team.subtitle')}
              members={teamMembers}
              isVisible={teamVisible}
              onPlayNote={playNote}
            />
          </section>

          {/* Contact CTA Section */}
          <section ref={ctaRef} id="contact">
            <ContactForm
              title={t('cta.title')}
              subtitle={t('cta.subtitle')}
              placeholder={t('cta.placeholder')}
              buttonText={t('cta.button')}
              isVisible={ctaVisible}
            />
          </section>

          {/* Footer */}
          <Footer
            companyName={t('footer.companyName')}
            companyNameZh={t('footer.companyNameZh')}
            slogan={t('footer.slogan')}
            copyright={t('footer.copyright')}
            navItems={footerNavItems}
          />
        </div>

        {/* Panel 3: Blog listing */}
        <div className="w-screen h-full">
          <BlogPanel onSlideBack={() => slideTo('landing')} />
        </div>
      </div>

      {/* Slide indicators */}
      <SlideIndicator
        direction="left"
        label={leftPanelLabel}
        onClick={slideLeft}
        visible={activePanelIdx > 0}
      />
      <SlideIndicator
        direction="right"
        label={rightPanelLabel}
        onClick={slideRight}
        visible={activePanelIdx < PANELS.length - 1}
      />

      {/* Login Portal Button - Fixed position */}
      <LoginPortalButton />

      {/* Chatbot */}
      <ChatBubble />
    </div>
  );
}
